import { Component, forwardRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import moment from 'moment-timezone';
import { filter, Subscription } from 'rxjs';

import { getDefaultDateInputOptionValuesModel } from '../../../../Common/Forms/models/date-query-input-options.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../models/date-query.model';
import { DateInputTypesEnum } from '../../enums/date-input-types.enum';
import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { DateHelper } from '../../helpers/date.helper';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { DatePickerComponent } from '../DatePicker/date-picker.component';


/**
 * ### Accessibility Usage
 * + ***ariaLabel*** - use this to provide an accessible label for the text input
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***tooltipLabel*** - add tooltip to the icon dropdown
 */
@Component({
    selector: 'agpr-date-query-input',
    templateUrl: './date-query-input.component.html',
    styleUrls: ['./date-query-input.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DateQueryInputComponent),
        multi: true,
    }],
    standalone: false
})
export class DateQueryInputComponent implements ControlValueAccessor, OnInit, OnDestroy {

    @Input() id: string = null;
    @Input() options: DropdownOptionModel[] = getDefaultDateInputOptionValuesModel();
    @Input() toolTipLabel: string;
    @Input() minimumCustomDate: Date = null;
    @Input() maximumCustomDate: Date = null;
    @Input() inputPlaceholder: string = '';
    @Input() isReadonly: boolean = false;
    @Input() icon: string;
    @Input() iconPopoverTemplate: TemplateRef<any>;
    @Input() iconPopoverStyleClass: string;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;
    @Input() ariaLabel: string;
    @Input() hideFormatHint: boolean = false;

    @ViewChild('calendar') calendar: DatePickerComponent;

    _onChange: Function = this.defaultOnChange;
    _onTouched: Function;

    calendarValue: any;

    dropdownValue: DropdownOptionModel = null;
    displayValue: string = '';
    isCustomDateRange: boolean = false;

    private value: DateQueryModel;

    private subscriptions: Subscription[] = [];
    private preferences: PreferencesModel;

    readonly agprComponentStyle = AgprComponentStyleEnum;

    constructor(
        public currentUserStateService: CurrentUserStateService
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
            })
        );
    }

    /**
     * Event triggered by dropdown after a selection happens
     */
    public dropdownSelected(event): void {
        const newType: DateInputTypesEnum = event.value.value.type;
        const newValue: DateQueryModel = event.value.value.query;
        switch (newType) {
            case DateInputTypesEnum.useValue:
                this.value = newValue;
                // A non-calendar value was chosen, reset the calendar
                this.isCustomDateRange = false;
                this.updateCalendarValue();
                this._onChange(this.value);
                this._onTouched();
                this.updateDisplayValue();
                break;
            case DateInputTypesEnum.custom:
                this.isCustomDateRange = true;
                requestAnimationFrame(() => {
                    this.calendar.showPanel();
                });
                break;
        }
    }

    /**
     * This is a separate, public function so that it can be tested
     */
    public defaultOnChange() {

    }

    /**
     * Event triggered by the calendar after it's closed
     */
    public calendarClosed(event): void {
        if (this.calendarValue && this.calendarValue[0]) {
            // There is a date in the calendar
            if (this.calendarValue[1]) {
                this.value = DateQueryModel.getAbsoluteQuery();
                // A range was selected
                this.value.absoluteRange = {
                    start: this.calendarValue[0],
                    end: moment(this.calendarValue[1]).endOf('day').toDate()
                };
                delete this.value.absoluteDate;
            } else {
                // A single value was selected
                this.value = DateQueryModel.getAbsoluteQuery();
                delete this.value.absoluteRange;
                this.value.absoluteDate = this.calendarValue[0];
            }
            this._onChange(this.value);
            this._onTouched();
            this.updateDisplayValue();
        }
    }

    /**
     * Called when something other than the dropdown sets the value, to update this.dropdownValue to reflect the new value
     */
    private updateDropdownValue(): void {
        const dropdownOption = this.options.find(
            (option) => {
                if (option && option.value) {
                    return DateQueryModel.equals(option.value.query, this.value);
                } else {
                    return false;
                }
            }
        );

        if (dropdownOption) {
            // Match from list of options
            this.dropdownValue = dropdownOption;
            this.updateCalendarValue();
        } else {
            // No match from list of options, could not exist, could be 'custom'
            const customOption = this.options.find((option) => option.value.type === DateInputTypesEnum.custom);
            if (customOption !== null &&
                customOption !== undefined &&
                customOption.value.query.type === this.value?.type
            ) {
                // If there's a custom option, none of the other options matched and the type of this matches the type of the custom option, choose custom
                this.dropdownValue = this.options.find((option) => option.value.type === DateInputTypesEnum.custom);
                this.updateCalendarValue();
            } else {
                this.dropdownValue = null;
            }
        }
    }

    /**
     * Sets the calendars value in cases that the value is written without involving the calendar
     */
    private updateCalendarValue(): void {
        if (this.value && this.value.type === DateQueryTypeEnum.ABSOLUTE) {
            this.isCustomDateRange = true;

            if (this.value.absoluteDate) {
                this.calendarValue = [this.value.absoluteDate];
            } else {
                this.calendarValue = [this.value.absoluteRange.start, this.value.absoluteRange.end];
            }
        } else {
            // This is "no value" to the calendar
            this.calendarValue = null;
            this.isCustomDateRange = false;
        }
    }

    /**
     * Sets the displayValue shown in the input box
     */
    private updateDisplayValue(): void {
        // No value
        if (!this.value || !this.dropdownValue) {
            this.displayValue = '';
            return;
        }

        // Has a value with a label, use the label
        if (this.dropdownValue.label && this.dropdownValue.value.type !== DateInputTypesEnum.custom) {
            this.displayValue = this.dropdownValue.label;
            return;
        }

        // Value must be absolute, set absolute display value
        if (this.value.absoluteDate) {
            this.displayValue = DateHelper.toDateString(this.value.absoluteDate, this.preferences?.timeZone || undefined);
        } else {
            this.displayValue = DateHelper.toDateString(this.value.absoluteRange.start, this.preferences?.timeZone || undefined)
                + ' - '
                + DateHelper.toDateString(this.value.absoluteRange.end, this.preferences?.timeZone || undefined);
        }
    }

    /**
     * ControlValueAccessor
     */
    public writeValue(value: DateQueryModel) {
        this.value = value;

        this.updateDropdownValue();

        this.updateDisplayValue();
    }

    public registerOnChange(onChange: Function) {
        this._onChange = onChange;
    }

    public registerOnTouched(onTouched: Function) {
        this._onTouched = onTouched;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
